import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';

import {
  SaveUserEmailPreferences,
  UserQueryEmailPreferences,
} from '../../../state/services/gql/user';
import { GQLQueryPropType } from '../../../proptypes';

import Container from '../../../components/Container';
import LoadingEllipsis from '../../../components/LoadingEllipsis';
import ToggleButtons from '../../../shared/ToggleButtons';

import {
  container,
  fields,
  primaryDetails,
  title,
  wrapper,
  row,
  column,
  columnRight,
} from './styles.module.css';

import { fieldset } from '../../../base-styles/forms.module.css';

const OnOffValues = [
  {
    value: 'On',
    label: 'On',
  },
  {
    value: 'Off',
    label: 'Off',
  },
];
const emailPrefs = [
  'BOOKING_LIFECYCLE',
  'RACE_RECOMMENDATIONS',
  'SAVED_EVENT_COUNTDOWN',
  'NEWSLETTER',
];

class EmailPrefs extends PureComponent {
  constructor(props) {
    super(props);
    this.state = emailPrefs.reduce(
      (acc, pref) => {
        return { ...acc, [pref]: OnOffValues[0].value };
      },
      {
        disabled: true,
        disableToggles: false,
      },
    );
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.emailPreferences) {
      const { disabled, unsubscribed } = nextProps.data.emailPreferences;
      this.setState({
        disableToggles: disabled === true,
        disabled: disabled === true ? 'Off' : 'On',
        BOOKING_LIFECYCLE: unsubscribed.includes('BOOKING_LIFECYCLE')
          ? 'Off'
          : 'On',
        RACE_RECOMMENDATIONS: unsubscribed.includes('RACE_RECOMMENDATIONS')
          ? 'Off'
          : 'On',
        SAVED_EVENT_COUNTDOWN: unsubscribed.includes('SAVED_EVENT_COUNTDOWN')
          ? 'Off'
          : 'On',
        NEWSLETTER: unsubscribed.includes('NEWSLETTER') ? 'Off' : 'On',
      });
    }
  }

  handleChange = (toggle) => {
    // Update state with new value and save
    this.setState({ [toggle.props.name]: toggle.state.value }, this.handleSave);

    // Enable/disable UI on master toggle
    if (toggle.props.name === 'disabled') {
      this.setState({ disableToggles: toggle.state.value === 'Off' });
    }
  };

  handleSave = () => {
    // Pull out the values we are interested in

    this.props.mutate({
      variables: {
        emailAddress: this.props.emailAddress,
        disabled: this.state.disabled === 'Off',
        unsubscribed: emailPrefs.filter((e) => this.state[e] !== 'On'),
      },
    });
  };

  render() {
    const { data } = this.props;

    if (data.loading) {
      return (
        <Container className={container}>
          <LoadingEllipsis htmlTag="h1" />
        </Container>
      );
    }

    const { emailAddress } =
      data.emailPreferences || data.variables.emailAddress;
    return (
      <div className={wrapper}>
        <div className={container}>
          <div className={title}>Email Settings</div>
          <div className={fieldset}>
            <div className={column}>
              <div>Your email </div>
              {emailAddress}
            </div>
          </div>
          <form className={fields}>
            <div className={primaryDetails}>
              <div className={row}>
                <div className={column}>
                  <div>Event recommendations</div>
                  Upcoming events recommended just for you
                  <br />
                  based on your interests and location.
                </div>
                <div className={columnRight}>
                  <ToggleButtons
                    name="RACE_RECOMMENDATIONS"
                    options={OnOffValues}
                    value={this.state.RACE_RECOMMENDATIONS}
                    onOptionSelect={this.handleChange}
                    disabled={this.state.disableToggles}
                  />
                </div>
              </div>
              <div className={row}>
                <div className={column}>
                  <div>Saved events</div>
                  When you save an event, we’ll keep you
                  <br />
                  updated with important news and reminders.
                </div>
                <div className={columnRight}>
                  <ToggleButtons
                    name="SAVED_EVENT_COUNTDOWN"
                    options={OnOffValues}
                    value={this.state.SAVED_EVENT_COUNTDOWN}
                    onOptionSelect={this.handleChange}
                    disabled={this.state.disableToggles}
                  />
                </div>
              </div>
              <div className={row}>
                <div className={column}>
                  <div>Advice emails in the run up to your event</div>
                  After you book a event, we’ll send you advice
                  <br />
                  and articles from our experts.
                </div>
                <div className={columnRight}>
                  <ToggleButtons
                    name="BOOKING_LIFECYCLE"
                    options={OnOffValues}
                    value={this.state.BOOKING_LIFECYCLE}
                    onOptionSelect={this.handleChange}
                    disabled={this.state.disableToggles}
                  />
                </div>
              </div>
              <div className={row}>
                <div className={column}>
                  <div>Newsletter</div>
                  We&#39;ll periodically send you emails with information <br />
                  about ballot openings, charity places on flagship <br />
                  events, and blog posts.
                </div>
                <div className={columnRight}>
                  <ToggleButtons
                    name="NEWSLETTER"
                    options={OnOffValues}
                    value={this.state.NEWSLETTER}
                    onOptionSelect={this.handleChange}
                    disabled={this.state.disableToggles}
                  />
                </div>
              </div>
              <div className={row}>
                <div className={column}>
                  <div>All email</div>
                  Control all emails from Let’s Do This. You’ll still
                  <br />
                  receive essential emails about your bookings.
                </div>
                <div className={columnRight}>
                  <ToggleButtons
                    name="disabled"
                    options={OnOffValues}
                    value={this.state.disabled}
                    onOptionSelect={this.handleChange}
                  />
                </div>
              </div>
              <div className={row}>
                <div className={column}>
                  <div>Essential emails</div>
                  Important emails regarding your account and
                  <br />
                  your bookings. You can’t turn off these emails.
                </div>
                <div className={columnRight}>
                  <ToggleButtons options={OnOffValues} disabled />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

EmailPrefs.propTypes = {
  /**
   * Data object from GraphQL query containing user query.
   */
  data: GQLQueryPropType.isRequired,
  /**
   * Callback function when user clicks any toggle button.
   */
  mutate: PropTypes.func.isRequired,
};

const EmailPrefsWithGraphQL = compose(
  graphql(UserQueryEmailPreferences, {
    options: (props) => {
      const { emailAddress } = props;
      return {
        variables: {
          emailAddress,
        },
      };
    },
  }),
  graphql(SaveUserEmailPreferences),
)(EmailPrefs);

export default EmailPrefsWithGraphQL;
